// Packages: one page for every package type, with Crates and Containers as filtered views (ADR-0035 section 11,
// ADR-0033 section 12, api.md 4.1). A container repository opens its image detail (`?repo=<rep_id>`).
import { useState } from "react";
import type { PackageItem, Pending, TenantDetail } from "../api/types";
import { useServices } from "../components/context";
import { IdCell } from "../components/IdCell";
import { PendingFilter, useGrace, type PendingRow } from "../components/Grace";
import { ErrorBanner, Section } from "../components/ui";
import { useLoad } from "../components/useLoad";
import { formatBytes, formatTime } from "../lib/format";
import { routeHash, type RouteType } from "../lib/router";
import { ImageDetail, repoDeleteTarget } from "./ImageDetail";

const TYPE_TABS: { label: string; type?: RouteType }[] = [
  { label: "All" },
  { label: "Crates", type: "cargo" },
  { label: "Containers", type: "oci" },
];

export const TYPE_LABEL: Record<PackageItem["type"], string> = { cargo: "crate", oci: "image" };

/** `type` is the filter from the route; no type shows all package types. `repo` opens the image detail. */
export function Packages({ tenant, type, repo }: { tenant: TenantDetail; type?: RouteType; repo?: string }) {
  if (repo) return <ImageDetail key={repo} tenant={tenant} repoId={repo} />;
  const title = type === "cargo" ? "Crates" : type === "oci" ? "Containers" : "Packages";
  return (
    <Section
      title={title}
      actions={
        <nav className="filter type-switch" aria-label="Package type">
          {TYPE_TABS.map((t) => (
            <a
              key={t.label}
              className="button-link"
              href={routeHash({ name: "tenant", tenant: tenant.slug, page: "packages", type: t.type })}
              aria-current={t.type === type ? "page" : undefined}
            >
              {t.label}
            </a>
          ))}
        </nav>
      }
    >
      {type === "cargo" ? (
        <CrateList tenant={tenant} />
      ) : (
        <PackageTable key={type ?? "all"} tenant={tenant} type={type} />
      )}
    </Section>
  );
}

/** One table for the packages list (api.md 4.1). Container repositories have delete, restore and purge. */
function PackageTable({ tenant, type }: { tenant: TenantDetail; type?: "oci" }) {
  const { api } = useServices();
  const [ns, setNs] = useState("");
  const list = useLoad(() => api.listPackages(tenant.slug, { type }), [tenant.slug, type]);
  const grace = useGrace<PackageItem>({
    tenant,
    onChange: list.reload,
    ops: {
      key: "oci-repositories",
      listPending: () =>
        api.listPackages(tenant.slug, { type: "oci", pending: true }) as Promise<{
          items: Pending<PackageItem>[];
        }>,
      remove: (t) => api.deleteOciRepository(tenant.slug, t.id, t.typedName ?? ""),
      restore: async (id) => void (await api.restoreOciRepository(tenant.slug, id)),
      purge: (id, confirm) => api.purgeOciRepository(tenant.slug, id, confirm),
    },
  });
  const items = (list.data?.items ?? []).filter((p) => !ns || p.namespace === ns);
  const namespaces = [...new Set((list.data?.items ?? []).map((p) => p.namespace))].sort();
  const pendingRows: PendingRow[] = grace.items.map((r) => ({
    id: r.id,
    title: (
      <span className="mono">
        {tenant.slug}/{r.namespace}/{r.name}
      </span>
    ),
    detail: `${r.versions_or_tags} tags, ${formatBytes(r.size_bytes)}`,
    deletedBy: r.deleted_by,
    deletedAt: r.deleted_at,
    purgeAfter: r.purge_after,
    purge: {
      id: r.id,
      title: `repository ${tenant.slug}/${r.namespace}/${r.name}`,
      cascade: "Its tags and manifests are deleted too.",
      confirm: r.name,
      confirmLabel: "repository name",
    },
  }));

  return (
    <>
      <div className="row list-head">
        <label className="inline">
          Namespace{" "}
          <select value={ns} onChange={(e) => setNs(e.target.value)}>
            <option value="">All</option>
            {namespaces.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <PendingFilter view={grace.view} setView={grace.setView} count={grace.count} />
      </div>
      <ErrorBanner error={list.error ?? grace.error} />
      {grace.view === "pending" ? (
        grace.rowsTable(pendingRows)
      ) : (
        <>
          <table className="stack data-table" aria-label={type === "oci" ? "Containers" : "Packages"}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Namespace</th>
                <th>Versions or tags</th>
                <th>Last push</th>
                <th>Size</th>
                <th>ID</th>
                <th className="actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id}>
                  <td>
                    <a
                      href={routeHash({
                        name: "tenant",
                        tenant: tenant.slug,
                        page: "packages",
                        type: p.type,
                        ...(p.type === "oci" ? { repo: p.id } : {}),
                      })}
                    >
                      <strong className="mono">{p.name}</strong>
                    </a>
                  </td>
                  <td data-label="Type">{TYPE_LABEL[p.type]}</td>
                  <td className="mono" data-label="Namespace">
                    {p.namespace}
                  </td>
                  <td data-label="Versions or tags">{p.versions_or_tags}</td>
                  <td data-label="Last push">{formatTime(p.last_publish_at)}</td>
                  <td data-label="Size">{formatBytes(p.size_bytes)}</td>
                  <IdCell id={p.id} />
                  <td className="actions" data-label="Actions">
                    {grace.isAdmin && p.type === "oci" && (
                      <button
                        type="button"
                        className="danger"
                        disabled={grace.busy}
                        onClick={() => grace.askDelete(repoDeleteTarget(tenant.slug, p))}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {list.data && items.length === 0 && (
            <p className="muted">
              {type === "oci"
                ? 'No container repositories yet. The first allowed push makes one. See "Connect".'
                : 'No packages yet. See "Connect" to publish the first one.'}
            </p>
          )}
        </>
      )}
      {grace.ui}
    </>
  );
}

/** Crates and versions in a tenant, with a namespace filter. */
function CrateList({ tenant }: { tenant: TenantDetail }) {
  const { api } = useServices();
  const [ns, setNs] = useState("");
  const nss = useLoad(() => api.listNamespaces(tenant.slug), [tenant.slug]);
  const list = useLoad(() => api.listCrates(tenant.slug, ns || undefined), [tenant.slug, ns]);
  const crates = list.data?.items ?? [];

  return (
    <>
      <div className="row list-head">
        <label className="inline">
          Namespace{" "}
          <select value={ns} onChange={(e) => setNs(e.target.value)}>
            <option value="">All</option>
            {(nss.data?.items ?? []).map((n) => (
              <option key={n.id} value={n.slug}>
                {n.slug}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="muted">Storage used: {formatBytes(tenant.storage_bytes)}.</p>
      <ErrorBanner error={list.error} />
      {!list.loading && crates.length === 0 && (
        <p className="muted">No crates yet. See "Connect" to publish the first one.</p>
      )}
      {crates.map((c) => (
        <details key={c.id} className="crate">
          <summary>
            <strong className="mono">{c.name}</strong> <span className="tag">{c.namespace}</span>{" "}
            <span className="muted">
              {c.versions.length} version{c.versions.length === 1 ? "" : "s"}
            </span>
          </summary>
          <table className="data-table">
            <thead>
              <tr>
                <th>Version</th>
                <th>Size</th>
                <th>Published</th>
                <th>By</th>
                <th>SHA-256</th>
              </tr>
            </thead>
            <tbody>
              {c.versions.map((v) => (
                <tr key={v.vers} className={v.yanked ? "yanked" : undefined}>
                  <td className="mono">
                    {v.vers} {v.yanked && <span className="tag denied">yanked</span>}
                  </td>
                  <td>{formatBytes(v.size_bytes)}</td>
                  <td>{formatTime(v.published_at)}</td>
                  <td className="mono small">{v.published_by}</td>
                  <td className="mono small" title={v.cksum}>
                    {v.cksum.slice(0, 12)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      ))}
    </>
  );
}
